import { useState } from 'react';
import { Trans } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { q } from '@actual-app/core/shared/query';

import { travesiaDeNuevo } from '#components/mobile/travesia/estado';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { useNavigate } from '#hooks/useNavigate';
import { saveSyncedPrefs } from '#prefs/prefsSlice';
import { aqlQuery } from '#queries/aqlQuery';
import { useDispatch, useStore } from '#redux';

/** ¿El presupuesto abierto tiene ya algún movimiento? */
async function hayMovimientos(): Promise<boolean> {
  try {
    const { data } = await aqlQuery(
      q('transactions').calculate({ $count: '*' }),
    );
    return Number(data) > 0;
  } catch {
    // Si no se sabe, lo prudente es no borrar nada.
    return true;
  }
}

/**
 * Ajustes → «Primera travesía»: vuelve a lanzar el tutorial de Gavi en el
 * presupuesto abierto. Deja la pref `travesia` pendiente en el paso 2 y
 * lleva al Plan, donde `PrimeraTravesia` la retoma. No borra lo que ya hay:
 * lo que se elija se añade.
 */
export function TravesiaSettings() {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const [ocupado, setOcupado] = useState(false);

  const onZarpar = async () => {
    if (ocupado) {
      return;
    }
    setOcupado(true);
    try {
      const nueva = travesiaDeNuevo(
        store.getState().prefs.synced.travesia,
        await hayMovimientos(),
      );
      await dispatch(saveSyncedPrefs({ prefs: { travesia: nueva } }));
      void navigate('/budget');
    } finally {
      setOcupado(false);
    }
  };

  return (
    <View style={{ gap: 12 }} data-testid="travesia-ajustes">
      <Text style={{ ...texto.secundario, color: color.fg2 }}>
        <Trans>
          Gavi guides you step by step: who you live with, your accounts, your
          categories and a goal. Nothing you already have is deleted; what you
          choose is added to this budget.
        </Trans>
      </Text>
      <Button
        variant="primary"
        data-testid="travesia-zarpar"
        isDisabled={ocupado}
        onPress={() => void onZarpar()}
        style={{ minHeight: TACTIL, borderRadius: radio.pildora }}
      >
        <Trans>Set sail with Gavi</Trans>
      </Button>
    </View>
  );
}
